import { BField } from '../Model/Field';
import { Encoder } from './Encoder';
function assignIdIfUnset(field) {
    return field.id ? field : assignId(field);
}
function assignId(field) {
    return { ...field, id: BField.GenerateID() };
}
export class BatchEncoder {
    constructor(encoding) {
        this._encoder = Encoder.newEncoder(encoding);
    }
    getFieldById(id) {
        return this._encoder.getFieldById(id);
    }
    getAllSiblingFields(id) {
        return this._encoder.getAllSiblingFields(id);
    }
    addEncField(field, channel, at) {
        field = assignIdIfUnset(field);
        this._encoder.addField(field, channel, at);
        this._encoder.autoEncode();
        return field;
    }
    createSharedAxisGroup(groupName, memberFieldId) {
        return this._encoder.createSharedAxisGroup(`GRP_${BField.GenerateID()}`, groupName, memberFieldId);
    }
    addEncFieldToGroup(field, groupId, index) {
        field = assignIdIfUnset(field);
        this._encoder.addEncFieldToGroup(field, groupId, index);
        return field;
    }
    removeFieldFromGroup(fieldId, channel, index) {
        this._encoder.removeFieldFromGroup(fieldId, channel, index);
    }
    moveEncFieldToGroup(fieldId, groupId, index) {
        this._encoder.moveEncFieldToGroup(fieldId, groupId, index);
    }
    removeEncFieldGroup(groupId) {
        this._encoder.removeEncFieldGroup(groupId);
    }
    moveGroup(groupId, channel, at) {
        this._encoder.moveGroup(groupId, channel, at);
    }
    toggleDualAxisOnGroup(groupId) {
        return this._encoder.toggleDualAxisOnGroup(groupId);
    }
    disbandGroup(groupId) {
        this._encoder.disbandGroup(groupId);
    }
    addEncFieldToSeries(field, channel, series, at) {
        if (series) {
            field = assignIdIfUnset(field);
            this._encoder.addFieldToSeries(series, field, channel, at);
            this._encoder.autoEncode();
            return [field.id];
        }
        const ids = [];
        this._encoder.addFieldToAllSeries(() => {
            const newField = assignId(field);
            ids.push(newField.id);
            return newField;
        }, channel, at);
        this._encoder.autoEncode();
        return ids;
    }
    addSort(sort) {
        this._encoder.addSort(sort);
    }
    removeSort(source) {
        this._encoder.removeSort(source);
    }
    clearSorts() {
        this._encoder.clearSorts();
    }
    addEncFilter(field, at) {
        field = assignIdIfUnset(field);
        this._encoder.addFilterField(field, at);
        return field;
    }
    mergeEncFilter(field) {
        field = assignIdIfUnset(field);
        this._encoder.mergeFilterField(field);
        return field;
    }
    appendEncFilter(field, at) {
        field = assignIdIfUnset(field);
        this._encoder.appendFilterField(field, at);
        return field;
    }
    removeFilter(source, discrete) {
        this._encoder.removeFilter(source, discrete);
    }
    clearFilters() {
        this._encoder.clearFilters();
    }
    setFrame(id, frame) {
        this._encoder.setFrame(id, frame);
    }
    clearFrame(id) {
        this._encoder.setFrame(id, undefined);
    }
    autoEncode() {
        this._encoder.autoEncode();
    }
    toggleDualAxisField(id) {
        return this._encoder.toggleDualAxis(id);
    }
    updateMarkType(type, isAutomatic, axis) {
        this._encoder.updateMarkType(type, isAutomatic !== null && isAutomatic !== void 0 ? isAutomatic : type == null, axis);
        if (type == null) {
            this._encoder.autoEncode();
        }
    }
    updateMarkColor(color, axis) {
        return this._encoder.updateMarkColor(color, axis);
    }
    replaceMarkColor(color, axis) {
        this._encoder.replaceMarkColor(color, axis);
    }
    updateMarkLabel(label, axis) {
        return this._encoder.updateMarkLabel(label, axis);
    }
    updateMarkSize(size, axis) {
        return this._encoder.updateMarkSize(size, axis);
    }
    updateMarkOptions(options, series) {
        return this._encoder.updateMarkOptions(options, series);
    }
    setTextDisplay(options, series) {
        return this._encoder.setTextDisplay(options, series);
    }
    updateField(query) {
        this._encoder.updateField(query);
        this._encoder.autoEncode();
    }
    removeField(id) {
        this._encoder.removeField(id);
        this._encoder.autoEncode();
    }
    removeFields(ids) {
        for (const id of ids) {
            this._encoder.removeField(id);
        }
        this._encoder.autoEncode();
    }
    moveField(id, channel, at, seriesId) {
        this._encoder.moveField(id, channel, at, seriesId);
        this._encoder.autoEncode();
    }
    swapRowsColumns() {
        this._encoder.swapRowsColumns();
        this._encoder.autoEncode();
    }
    enableGrandTotal(direction) {
        this._encoder.enableGrandTotal(direction);
    }
    disableGrandTotal(direction) {
        this._encoder.disableGrandTotal(direction);
    }
    enableSubtotals(direction) {
        this._encoder.enableSubtotals(direction);
    }
    disableSubtotals(direction) {
        this._encoder.disableSubtotals(direction);
    }
    complete() {
        return {
            encoding: this._encoder.serialize(),
            summary: this._encoder.getMarksRollup(),
        };
    }
    get __encoder() {
        return this._encoder;
    }
}
export class Guide {
    get encoder() {
        return this._encoder.__encoder;
    }
    addEncField(encoding, field, channel, at) {
        return this.encode(encoding, (encoder) => encoder.addEncField(field, channel, at));
    }
    addEncFieldToSeries(encoding, field, channel, series, at) {
        return this.encode(encoding, (encoder) => encoder.addEncFieldToSeries(field, channel, series, at));
    }
    createSharedAxisGroup(encoding, groupName, memberFieldId) {
        return this.encode(encoding, (encoder) => encoder.createSharedAxisGroup(groupName, memberFieldId));
    }
    addEncFieldToGroup(encoding, field, groupId, index) {
        return this.encode(encoding, (encoder) => encoder.addEncFieldToGroup(field, groupId, index));
    }
    removeFieldFromGroup(encoding, fieldId, channel, index) {
        return this.encode(encoding, (encoder) => encoder.removeFieldFromGroup(fieldId, channel, index));
    }
    removeEncFieldGroup(encoding, groupId) {
        return this.encode(encoding, (encoder) => encoder.removeEncFieldGroup(groupId));
    }
    moveGroup(encoding, groupId, channel, at) {
        return this.encode(encoding, (encoder) => encoder.moveGroup(groupId, channel, at));
    }
    moveEncFieldToGroup(encoding, fieldId, groupId, index) {
        return this.encode(encoding, (encoder) => encoder.moveEncFieldToGroup(fieldId, groupId, index));
    }
    toggleDualAxisOnGroup(encoding, groupId) {
        return this.encode(encoding, (encoder) => encoder.toggleDualAxisOnGroup(groupId));
    }
    disbandGroup(encoding, groupId) {
        return this.encode(encoding, (encoder) => encoder.disbandGroup(groupId));
    }
    addSort(encoding, sort) {
        return this.encode(encoding, (encoder) => encoder.addSort(sort));
    }
    removeSort(encoding, fieldId) {
        return this.encode(encoding, (encoder) => encoder.removeSort(fieldId));
    }
    clearSorts(encoding) {
        return this.encode(encoding, (encoder) => encoder.clearSorts());
    }
    addEncFilter(encoding, field, at) {
        return this.encode(encoding, (encoder) => encoder.addEncFilter(field, at));
    }
    mergeEncFilter(encoding, field) {
        return this.encode(encoding, (encoder) => encoder.mergeEncFilter(field));
    }
    appendEncFilter(encoding, field, at) {
        return this.encode(encoding, (encoder) => encoder.appendEncFilter(field, at));
    }
    removeFilter(encoding, source, discrete) {
        return this.encode(encoding, (encoder) => encoder.removeFilter(source, discrete));
    }
    clearFilters(encoding) {
        return this.encode(encoding, (encoder) => encoder.clearFilters());
    }
    setFrame(encoding, id, frame) {
        return this.encode(encoding, (encoder) => encoder.setFrame(id, frame));
    }
    clearFrame(encoding, id) {
        return this.encode(encoding, (encoder) => encoder.clearFrame(id));
    }
    toggleDualAxisField(encoding, id) {
        return this.encode(encoding, (encoder) => encoder.toggleDualAxisField(id));
    }
    updateMarkType(encoding, type, axis) {
        return this.encode(encoding, (encoder) => encoder.updateMarkType(type, type == null, axis));
    }
    updateMarkColor(encoding, color, axis) {
        return this.encode(encoding, (encoder) => encoder.updateMarkColor(color, axis));
    }
    replaceMarkColor(encoding, color, axis) {
        this.encode(encoding, (encoder) => encoder.replaceMarkColor(color, axis));
    }
    updateMarkLabel(encoding, label, axis) {
        return this.encode(encoding, (encoder) => encoder.updateMarkLabel(label, axis));
    }
    updateMarkSize(encoding, size, axis) {
        return this.encode(encoding, (encoder) => encoder.updateMarkSize(size, axis));
    }
    updateMarkOptions(encoding, options, series) {
        return this.encode(encoding, (encoder) => encoder.updateMarkOptions(options, series));
    }
    setTextDisplay(encoding, display, series) {
        return this.encode(encoding, (encoder) => encoder.setTextDisplay(display, series));
    }
    updateField(encoding, query) {
        return this.encode(encoding, (encoder) => encoder.updateField(query));
    }
    removeField(encoding, id) {
        return this.encode(encoding, (encoder) => encoder.removeField(id));
    }
    removeFields(encoding, ids) {
        return this.encode(encoding, (encoder) => encoder.removeFields(ids));
    }
    swapRowsColumns(encoding) {
        return this.encode(encoding, (encoder) => encoder.swapRowsColumns());
    }
    load(encoding) {
        return this.encode(encoding, (encoder) => encoder.autoEncode());
    }
    enableGrandTotal(encoding, direction) {
        return this.encode(encoding, (batch) => {
            batch.enableGrandTotal(direction);
        });
    }
    disableGrandTotal(encoding, direction) {
        return this.encode(encoding, (batch) => {
            batch.disableGrandTotal(direction);
        });
    }
    enableSubtotals(encoding, direction) {
        return this.encode(encoding, (batch) => {
            batch.enableSubtotals(direction);
        });
    }
    disableSubtotals(encoding, direction) {
        return this.encode(encoding, (batch) => {
            batch.disableSubtotals(direction);
        });
    }
    newBatchOperation(encoding) {
        return new BatchEncoder(encoding);
    }
    encode(encoding, func) {
        this._encoder = new BatchEncoder(encoding);
        func(this._encoder);
        return this._encoder.complete();
    }
}
